import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Loader } from 'lucide-react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DefaultThumbnail from '../../../../assets/recording/WhiteThumbnail.svg';
import type { FileType, FolderType } from '../../../../types';
import FileActionMenu from '../FileActionMenu';
import { RootState } from '../../../../reducers';
import { useCachedThumbnail } from '../../../../hooks/useCachedThumbnail';
import { Skeleton } from '../../../../components/ui/skeleton';
import { Checkbox } from '../../../../components/ui/checkbox';
import { isDemoVideo } from '../../../../constants/demoVideo';
import { pushModal } from '../../../globalModals/globalModalsSlice';
import { getCachedImportUrl } from '../../../../middleware/socketMiddleware/handleUpdate';
import { formatDuration } from '../../../../lib/videoUtils';
import { FileGridItemShell } from '../components/FileGridItemShell';
import { FolderSelector } from '../components/FolderSelector';
import { selectVideoFromLibrary } from '../../../selectedVideo/selectedVideoSlice';

// Preload the SelectedVideo component (debounced at module level)
let preloadPromise: Promise<any> | null = null;
const preloadSelectedVideo = () => {
  if (!preloadPromise) {
    preloadPromise = import('../../../selectedVideo');
  }
  return preloadPromise;
};

interface FileProps {
  file: FileType;
  isSelected: boolean;
  showCheckbox: boolean;
  onSelect: (file: FileType) => void;
  fileUrl: string;
  isMobile?: boolean;
}

// Native date formatting - replaces Moment.js for better performance
const getCreatedAtString = (createdAt: Date) => {
  const now = new Date();
  const created = new Date(createdAt);

  // Check if valid date
  if (Number.isNaN(created.getTime())) {
    return String(createdAt);
  }

  // Calculate difference in days
  const diffMs = now.getTime() - created.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  }

  if (diffDays === 1) {
    return 'Yesterday';
  }

  if (diffDays === 2) {
    return '2 days ago';
  }

  if (diffDays === 3) {
    return '3 days ago';
  }

  // Use native Intl for formatting
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(created);
};

// Minimal processing card - Shadcn 2025
const ProcessingGridView = ({ name, url }: { name: string; url?: string }) => {
  // Debug logging
  React.useEffect(() => {
    if (url) {
      console.log('ProcessingGridView - URL:', url);
    }
  }, [url]);

  return (
    <FileGridItemShell name={name} youtubeUrl={url} statusText="Importing..." />
  );
};

const File = React.memo(
  ({
    file,
    isSelected,
    showCheckbox,
    onSelect,
    fileUrl,
    isMobile = false,
  }: FileProps) => {
    const {
      name,
      thumbUrl,
      createdAt,
      duration,
      type,
      _id,
      parentId,
      url,
      ownerId,
    } = file;
    const [isHovering, setIsHovering] = useState(false);
    const longPressTimer = useRef<ReturnType<typeof setTimeout>>();
    const touchStartTime = useRef<number>(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Get current user ID to determine if file is truly private
    const currentUserId = useSelector(
      (state: RootState) => state.settings.user?._id,
    );

    // Use cached thumbnail hook
    const { thumbnailSrc, isLoading, handleLoad, handleError } =
      useCachedThumbnail({
        fileId: _id,
        thumbnailUrl: thumbUrl,
        fallbackUrl: DefaultThumbnail,
      });

    // Check if the file type is Import
    const isImportedFile = type === 'Import';

    // Get URL from cache if not provided by backend
    const importUrl =
      url || (isImportedFile ? getCachedImportUrl(_id) : undefined);

    // Debug logging for Import files
    useEffect(() => {
      if (isImportedFile) {
        console.log('Import file detected:', {
          name,
          type,
          url,
          cachedUrl: getCachedImportUrl(_id),
          finalUrl: importUrl,
          hasUrl: !!importUrl,
        });
      }
    }, [isImportedFile, name, type, url, _id, importUrl]);

    const handleCheckboxClick = (e: React.MouseEvent) => {
      // Don't allow selecting demo video
      if (isDemoVideo(_id)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (onSelect) {
        e.preventDefault();
        e.stopPropagation();
        onSelect(file);
      }
    };

    const handleCheckboxChange = () => {
      // Don't allow selecting demo video
      if (isDemoVideo(_id)) {
        return;
      }
      if (onSelect) {
        onSelect(file);
      }
    };

    const handleTouchStart = () => {
      touchStartTime.current = Date.now();
      longPressTimer.current = setTimeout(() => {
        setIsHovering(true);
      }, 500);
    };

    const handleTouchEnd = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };

    const handleTouchMove = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };

    // Combine Redux selectors for better performance
    const { parentFolder, isMoving } = useSelector(
      (state: RootState) => ({
        parentFolder: state.library.folders.data.find(
          (folder: FolderType) => folder._id === parentId,
        ),
        isMoving: state.library.movingFileIds.includes(_id),
      }),
      shallowEqual,
    );

    const { folderId } = useParams<{ folderId: string }>();
    const isInRecentsView = folderId === 'recents';
    const hasFolder = parentId !== null;

    const handleMoveClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(pushModal({ type: 'move-file-modal', files: [file] }));
      },
      [dispatch, file],
    );

    // Drag and drop handlers for adding files to chat
    const handleDragStart = (e: React.DragEvent) => {
      // Store the file data as JSON
      e.dataTransfer.setData('application/json', JSON.stringify(file));
      e.dataTransfer.effectAllowed = 'all';
      setIsDragging(true);

      // Set cursor style on document body during drag
      document.body.style.cursor = 'grabbing';

      // Create a simple, small drag preview with thumbnail
      const dragPreview = document.createElement('div');
      dragPreview.className =
        'bg-background border border-border rounded-lg shadow-lg p-2 flex items-center gap-2 max-w-[220px]';
      dragPreview.style.cursor = 'grabbing';

      // Create thumbnail element
      const thumbImg = document.createElement('img');
      thumbImg.src = thumbnailSrc;
      thumbImg.className = 'w-8 h-8 rounded object-cover flex-shrink-0';
      if (thumbnailSrc === DefaultThumbnail) {
        thumbImg.className += ' dark:invert';
      }

      // Create text container
      const textSpan = document.createElement('span');
      textSpan.className = 'text-sm font-medium truncate';
      textSpan.textContent = name;

      dragPreview.appendChild(thumbImg);
      dragPreview.appendChild(textSpan);

      dragPreview.style.position = 'absolute';
      dragPreview.style.top = '-1000px';
      document.body.appendChild(dragPreview);
      e.dataTransfer.setDragImage(dragPreview, 0, 0);
      setTimeout(() => document.body.removeChild(dragPreview), 0);
    };

    const handleDragEnd = () => {
      setIsDragging(false);
      setIsMouseDown(false);
      // Reset cursor on body
      document.body.style.cursor = '';
    };

    const handleMouseDown = () => {
      if (!showCheckbox) {
        setIsMouseDown(true);
      }
    };

    const handleMouseUp = () => {
      setIsMouseDown(false);
    };

    // Prevent default drag behavior on images and links
    const preventDefaultDrag = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Memoized mouse event handlers
    const handleMouseEnter = useCallback(() => {
      if (!isMobile) {
        setIsHovering(true);
        // Preload the SelectedVideo component on hover for smooth animation
        preloadSelectedVideo();
      }
    }, [isMobile]);

    const handleMouseLeave = useCallback(() => {
      if (!isMobile) {
        setIsHovering(false);
      }
    }, [isMobile]);

    // Handle click navigation with cache pre-loading
    const handleCardClick = useCallback(
      (e: React.MouseEvent) => {
        // Don't navigate if clicking on interactive elements
        const target = e.target as HTMLElement;

        // Check if clicking on checkbox overlay
        if (target.closest('[data-checkbox-overlay]')) {
          return;
        }

        // Check if clicking on action menu
        if (
          target.closest('button[data-action-menu]') ||
          target.closest('[data-radix-dropdown-menu-trigger]') ||
          target.closest('[data-radix-dropdown-menu-content]') ||
          target.closest('[role="menuitem"]')
        ) {
          return;
        }

        // Check if clicking on folder selector
        if (target.closest('[data-folder-selector]')) {
          return;
        }

        // Pre-load the cached file data into selectedVideo state before navigation
        dispatch(selectVideoFromLibrary(file));

        // Navigate to the file
        navigate(fileUrl);
      },
      [dispatch, file, fileUrl, navigate],
    );

    if (isImportedFile) {
      return <ProcessingGridView name={name} url={importUrl} />;
    }

    // Determine cursor style
    let cursorStyle = 'cursor-pointer';
    if (isDragging) {
      cursorStyle = 'opacity-50 cursor-grabbing';
    } else if (isMouseDown) {
      cursorStyle = 'cursor-grabbing';
    }

    // Show checkbox when hovering OR when in select mode
    const shouldShowCheckbox = isHovering || showCheckbox;

    // Modern minimal card - Shadcn 2025
    return (
      <motion.div
        layoutId={`video-card-${_id}`}
        layout="position"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          layout: {
            duration: 0.15,
            ease: [0.25, 0.1, 0.25, 1],
          },
          opacity: { duration: 0.1, ease: 'easeOut' },
        }}
        draggable={!showCheckbox}
        onDragStart={handleDragStart as any}
        onDragEnd={handleDragEnd as any}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onClick={handleCardClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-testid="video-grid-tile"
        className={`group relative flex flex-col h-full border border-border rounded-lg overflow-hidden bg-white dark:bg-background ${cursorStyle}`}
        style={{
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden' as const,
          WebkitFontSmoothing: 'subpixel-antialiased' as const,
        }}
      >
        {/* Thumbnail with zoom on hover */}
        <div className="relative overflow-hidden">
          {/* Checkbox overlay - minimal */}
          {shouldShowCheckbox && !isDemoVideo(_id) && (
            <div
              className="absolute top-2 left-2 z-10"
              data-checkbox-overlay
              onClick={handleCheckboxClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCheckboxClick(e as any);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={handleCheckboxChange}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4 bg-background/90 backdrop-blur-sm"
              />
            </div>
          )}

          <div className="block">
            {isLoading && thumbnailSrc !== thumbUrl && (
              <Skeleton className="w-full aspect-video absolute inset-0 bg-white dark:bg-black" />
            )}
            <img
              key={_id}
              src={thumbnailSrc}
              alt=""
              loading="eager"
              decoding="async"
              draggable={false}
              onDragStart={preventDefaultDrag}
              className={`w-full aspect-video object-cover group-hover:scale-105 ${
                thumbnailSrc === DefaultThumbnail ? 'dark:invert bg-white' : ''
              } ${isLoading && thumbnailSrc !== thumbUrl ? 'opacity-0' : 'opacity-100'}`}
              style={{
                transition:
                  'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out',
                transform: 'translateZ(0)',
                willChange: isHovering ? 'transform' : 'auto',
              }}
              onLoad={handleLoad}
              onError={handleError}
            />
            {/* Duration badge - always visible */}
            <div
              className={`absolute bottom-2 right-2 px-1.5 py-0.5 text-xs font-medium rounded backdrop-blur-sm ${
                thumbnailSrc === DefaultThumbnail
                  ? 'bg-muted/90 text-foreground'
                  : 'bg-black/70 text-white'
              }`}
            >
              {formatDuration(duration)}
            </div>
          </div>
        </div>

        {/* Content - flex-grow ensures consistent height */}
        <div className="p-3 relative flex-grow flex flex-col">
          <div className="block text-inherit no-underline flex-grow pr-10">
            {/* Title - always takes exactly 2 lines worth of space */}
            <h3
              data-testid="video-grid-tile-name"
              className="text-sm font-medium line-clamp-2 leading-snug mb-1.5 min-h-[2.5rem]"
            >
              {name}
            </h3>

            {/* Date and folder info */}
            {isInRecentsView ? (
              <div className="flex items-center gap-1 text-xs text-muted-foreground overflow-hidden">
                <div data-folder-selector className="min-w-0 truncate">
                  <FolderSelector
                    hasFolder={hasFolder}
                    folderName={parentFolder?.name}
                    onClick={handleMoveClick}
                    ownerId={ownerId}
                    currentUserId={currentUserId}
                  />
                </div>
                <span className="flex-shrink-0">·</span>
                <span
                  data-testid="video-grid-tile-date"
                  className="flex-shrink-0 whitespace-nowrap"
                >
                  {getCreatedAtString(createdAt).toString()}
                </span>
              </div>
            ) : (
              <p
                data-testid="video-grid-tile-date"
                className="text-xs text-muted-foreground"
              >
                {getCreatedAtString(createdAt).toString()}
              </p>
            )}
          </div>

          {/* Action menu - only on hover */}
          <div
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
            data-action-menu
          >
            <FileActionMenu file={file} />
          </div>
        </div>

        {/* Loading overlay when file is being moved */}
        {isMoving && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-10">
            <Loader className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}
      </motion.div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison for memo - only re-render if these props change
    return (
      prevProps.file._id === nextProps.file._id &&
      prevProps.file.name === nextProps.file.name &&
      prevProps.file.thumbUrl === nextProps.file.thumbUrl &&
      prevProps.file.type === nextProps.file.type &&
      prevProps.file.parentId === nextProps.file.parentId &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.showCheckbox === nextProps.showCheckbox &&
      prevProps.fileUrl === nextProps.fileUrl &&
      prevProps.isMobile === nextProps.isMobile
    );
  },
);

export default File;
